import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, signalStoreFeature, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Billing, LicenseTypeDto } from 'mbs-ui-shared@components/components/models/license.model';
import { AvailableLicenseType, LicenseTypeSummary } from 'mbs-ui-shared@models/License';
import { LicenseType } from 'mbs-ui-shared@models/LicenseType.enum';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { filter, pipe, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

// ---------------------------------------------------------------------------
// Common section
// ---------------------------------------------------------------------------

function withLicensesCommonFeature() {
  return signalStoreFeature(
    withState({
      allLicenseTypes: null as LicenseTypeSummary[] | null,
      allLicenseTypesLoading: false,
      allLicenseTypesLoaded: false,

      licenseTypesByOwner: null as LicenseType[] | null,
      licenseTypesByOwnerLoading: false,
      licenseTypesByOwnerLoaded: false,

      supportedLicenseTypes: null as AvailableLicenseType[] | null,
      supportedLicenseTypesLoading: false,
      supportedLicenseTypesLoaded: false
    }),
    withMethods((store, licensesService = inject(LicensesService)) => ({
      loadAllLicenseTypes: rxMethod<boolean>(
        pipe(
          filter((force) => force || !store.allLicenseTypesLoaded()),
          tap(() => patchState(store, { allLicenseTypesLoading: true })),
          switchMap(() =>
            licensesService.getTypes().pipe(
              tapResponse({
                next: (types) =>
                  patchState(store, {
                    allLicenseTypes: types,
                    allLicenseTypesLoaded: true,
                    allLicenseTypesLoading: false
                  }),
                error: () => patchState(store, { allLicenseTypesLoading: false })
              })
            )
          )
        )
      ),

      loadLicenseTypesByOwner: rxMethod<boolean>(
        pipe(
          filter((force) => force || !store.licenseTypesByOwnerLoaded()),
          tap(() => patchState(store, { licenseTypesByOwnerLoading: true })),
          switchMap(() =>
            licensesService.getTypesByOwnerId().pipe(
              tapResponse({
                next: (types) =>
                  patchState(store, {
                    licenseTypesByOwner: types,
                    licenseTypesByOwnerLoaded: true,
                    licenseTypesByOwnerLoading: false
                  }),
                error: () => patchState(store, { licenseTypesByOwnerLoading: false })
              })
            )
          )
        )
      ),

      loadSupportedLicenseTypes: rxMethod<{ hid: string; kind: number; companyId?: string; userId?: string }>(
        pipe(
          tap(() => patchState(store, { supportedLicenseTypesLoading: true })),
          switchMap(({ hid, kind, companyId, userId }) =>
            licensesService.getSupportedLicenseTypes(hid, kind, companyId, userId).pipe(
              tapResponse({
                next: (types) =>
                  patchState(store, {
                    supportedLicenseTypes: types,
                    supportedLicenseTypesLoaded: true,
                    supportedLicenseTypesLoading: false
                  }),
                error: () => patchState(store, { supportedLicenseTypesLoading: false })
              })
            )
          )
        )
      )
    }))
  );
}

// ---------------------------------------------------------------------------
// Prepayment section
// ---------------------------------------------------------------------------

function withLicensesPrepaymentFeature() {
  return signalStoreFeature(
    withState({
      prepaymentBilling: null as Billing | null,
      prepaymentBillingLoading: false,
      prepaymentBillingLoaded: false,

      licenseTypesForHistoryByOwner: null as LicenseType[] | null,
      licenseTypesForHistoryByOwnerLoading: false,
      licenseTypesForHistoryByOwnerLoaded: false
    }),
    withMethods((store, licensesService = inject(LicensesService)) => ({
      loadPrepaymentBilling: rxMethod<boolean>(
        pipe(
          filter((force) => force || !store.prepaymentBillingLoaded()),
          tap(() => patchState(store, { prepaymentBillingLoading: true })),
          switchMap(() =>
            licensesService.getUpcomingRenew().pipe(
              tapResponse({
                next: (billing) =>
                  patchState(store, {
                    prepaymentBilling: billing,
                    prepaymentBillingLoaded: true,
                    prepaymentBillingLoading: false
                  }),
                error: () => patchState(store, { prepaymentBillingLoading: false })
              })
            )
          )
        )
      ),

      loadLicenseTypesForHistoryByOwner: rxMethod<boolean>(
        pipe(
          filter((force) => force || !store.licenseTypesForHistoryByOwnerLoaded()),
          tap(() => patchState(store, { licenseTypesForHistoryByOwnerLoading: true })),
          switchMap(() =>
            licensesService.getTypesForHistoryByOwnerId().pipe(
              tapResponse({
                next: (types) =>
                  patchState(store, {
                    licenseTypesForHistoryByOwner: types,
                    licenseTypesForHistoryByOwnerLoaded: true,
                    licenseTypesForHistoryByOwnerLoading: false
                  }),
                error: () => patchState(store, { licenseTypesForHistoryByOwnerLoading: false })
              })
            )
          )
        )
      )
    }))
  );
}

// ---------------------------------------------------------------------------
// Postpayment section (placeholder for future postpayment state)
// ---------------------------------------------------------------------------

function withLicensesPostpaymentFeature() {
  return signalStoreFeature(
    withState({
      ppProviderLicensesTypesDto: null as LicenseTypeDto[] | null,
      ppProviderLicensesTypesLoading: false,
      ppProviderLicensesTypesLoaded: false
    }),
    withMethods((store, licensesService = inject(LicensesService)) => ({
      loadPPProviderLicensesTypes: rxMethod<boolean>(
        pipe(
          filter((force) => force || !store.ppProviderLicensesTypesLoaded()),
          tap(() => patchState(store, { ppProviderLicensesTypesLoading: true })),
          switchMap(() =>
            licensesService.getPPProviderLicensesTypes().pipe(
              tapResponse({
                next: (types) =>
                  patchState(store, {
                    ppProviderLicensesTypesDto: types,
                    ppProviderLicensesTypesLoaded: true,
                    ppProviderLicensesTypesLoading: false
                  }),
                error: () => patchState(store, { ppProviderLicensesTypesLoading: false })
              })
            )
          )
        )
      )
    }))
  );
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const LicensesSignalStore = signalStore(
  { providedIn: 'root' },
  withDevtools('LicensesSignalStore'),
  withLicensesCommonFeature(),
  withLicensesPrepaymentFeature(),
  withLicensesPostpaymentFeature()
);

export type LicensesSignalStoreType = InstanceType<typeof LicensesSignalStore>;
